import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Field, ImageUpload, SwitchRow } from "@/components/editor/fields";
import { api, formatErr, PUBLIC_BASE_URL } from "@/lib/api";

const SlugEditor = ({ card, onSlugChanged }) => {
  const [slug, setSlug] = useState(card.slug);
  const [history, setHistory] = useState([]);
  const load = () => api.get(`/cards/${card.id}/slugs`).then((r) => setHistory(r.data));
  useEffect(() => {
    load();
    setSlug(card.slug);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [card.id, card.slug]);
  const save = async () => {
    try {
      const { data } = await api.put(`/cards/${card.id}/slug`, { slug });
      toast.success("Indirizzo aggiornato. Il vecchio URL reindirizza al nuovo.");
      onSlugChanged(data.slug);
    } catch (e) {
      toast.error(formatErr(e));
    }
  };
  const old = history.filter((h) => !h.is_primary);
  return (
    <div className="space-y-2">
      <div className="eyebrow">Indirizzo pubblico</div>
      <div className="flex items-center gap-2">
        <span className="shrink-0 font-mono-ui text-xs text-zinc-400">/c/</span>
        <Input data-testid="slug-input" value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))} className="bg-white font-mono-ui text-sm" />
        <Button variant="outline" disabled={slug === card.slug} onClick={save} data-testid="slug-save-button">Cambia</Button>
      </div>
      <p className="text-[11px] text-zinc-500 break-all">{PUBLIC_BASE_URL}/c/{card.slug}</p>
      {card.status === "published" && (
        <p className="flex gap-1.5 text-[11px] text-amber-700"><AlertTriangle className="h-3.5 w-3.5 shrink-0" />La card è pubblicata: se cambi indirizzo, il precedente resterà attivo come redirect per non invalidare i QR già stampati.</p>
      )}
      {old.length > 0 && (
        <div className="rounded-lg border bg-white p-2.5 text-[11px] text-zinc-500" data-testid="slug-redirects">
          <div className="mb-1 font-medium text-zinc-700">Redirect attivi verso questa card</div>
          {old.map((h) => <div key={h.slug} className="font-mono-ui">/c/{h.slug} → /c/{card.slug}</div>)}
        </div>
      )}
    </div>
  );
};

export const SettingsPanel = ({ card, update, onSlugChanged }) => {
  const s = card.settings;
  return (
    <div className="space-y-6" data-testid="editor-settings-tab">
      <Field label="Nome interno della card" value={card.name} onChange={(v) => update("name", v)} testId="card-name-input" />
      <SlugEditor card={card} onSlugChanged={onSlugChanged} />
      <SwitchRow label="Footer brand" hint={'Mostra "Digital card by [brand]" in fondo alla pagina'} checked={s.show_footer} onChange={(v) => update("settings.show_footer", v)} testId="footer-toggle" />
      <div className="space-y-3">
        <div className="eyebrow">SEO e condivisione</div>
        <Field label="Titolo pagina" value={s.seo_title} onChange={(v) => update("settings.seo_title", v)} testId="seo-title-input" placeholder="Predefinito: Nome · Ruolo · Azienda" />
        <Field label="Descrizione" area rows={2} value={s.seo_description} onChange={(v) => update("settings.seo_description", v)} testId="seo-description-input" placeholder="Predefinita: biografia" />
        <ImageUpload label="Immagine di anteprima (Open Graph)" aspect="wide" value={s.og_image_url} onChange={(v) => update("settings.og_image_url", v)} testId="og-image-upload" />
      </div>
    </div>
  );
};
